import { gql } from "@apollo/client";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Close from "@mui/icons-material/Close";
import { formatISO, set } from "date-fns";
import _ from "lodash";
import { useState } from "react";

import StartEndTime from "~/components/common/forms/StartEndTime";
import { useSnackbar } from "~/hooks/useSnackbar";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

import { CommandIndexProvidersQuery, ShiftProviderTravelEnum, useCreateShiftMutation } from "~/graphql/__generated__/graphql";
import Help from "../common/Help";
import LabeledSelect from "../common/forms/LabeledSelect";

type AddShiftProps = {
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  date: Date;
  onClose: () => void;
  /** Pre-select a specific provider (e.g. when taking an unassigned visit) */
  initialProviderId?: number;
  /** Pre-populate shift start time */
  initialStartTime?: Date;
  /** Pre-populate shift end time */
  initialEndTime?: Date;
  /** Called after a shift is successfully created (in addition to onClose) */
  onShiftCreated?: () => void;
};

const AddShift = ({ date, onClose, providers, initialProviderId, initialStartTime, initialEndTime, onShiftCreated }: AddShiftProps) => {
  const orgConfig = useOrgConfig();
  const { setErrorMessage: displayError } = useSnackbar();

  const [createShift, { loading }] = useCreateShiftMutation();
  const [startTime, setStartTime] = useState<Date>(() => initialStartTime ?? set(date, {hours: 9, minutes: 0, seconds: 0, milliseconds: 0}));
  const [endTime, setEndTime] = useState<Date>(() => initialEndTime ?? set(date, {hours: 17, minutes: 0, seconds: 0, milliseconds: 0}));
  const [providerId, setProviderId] = useState<number>(initialProviderId ?? providers[0]?.id);
  const [providerTravel, setProviderTravel] = useState<ShiftProviderTravelEnum | "">("");
  const [shiftCenterId, setShiftCenterId] = useState<number | null>(null);
  const [overflowShift, setOverflowShift] = useState<boolean>(false);
  const [useShiftCenterDriveTimes, setUseShiftCenterDriveTimes] = useState<boolean>(false);
  
  const provider = providers.find(p => p.id === providerId);

  if (!providers.length || !providerId || !provider) { 
    return (
      <Card elevation={3} sx={{ width: 600 }}>
        <Header onClose={onClose} />
        <CardContent>
          <Grid container direction="row" spacing={2} sx={{ mt: 3 }}>
            <Grid size={5}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
            <Grid size={7}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: "end", borderTop: 1, borderColor: "divider" }}>
          <Skeleton variant="rectangular" width={100} height={36} />
        </CardActions>
      </Card>
    );
  }
  
  const shiftLength = endTime.getTime() - startTime.getTime();
  const shiftTooShort = shiftLength < 3600 * 1000;

  const onSave = async () => {
    if (!providerTravel) { return; }
    try {
      await createShift({
        variables: {
          startTime: formatISO(startTime),
          endTime: formatISO(endTime),
          providerId,
          addressStreet: provider.addressStreet,
          addressLineTwo: provider.addressLineTwo || "",
          addressCity: provider.addressCity,
          addressState: provider.addressState,
          addressZip: provider.addressZip,
          addressCoordinates: provider.addressCoordinates || "",
          addressTimezone: provider.addressTimezone,
          providerTravel,
          shiftCenterId,
          overflowShift,
          useShiftCenterDriveTimes,
        },
        refetchQueries: ["CommandIndexCombinedData", "AvailabilityByTimespan", "VisitAlerts", "VisitLocations"],
        awaitRefetchQueries: true,
      });
      onShiftCreated?.();
      onClose();
    } catch (error) {
      console.error("Error adding shift:", error);
      displayError("Adding shift failed.");
    }
  };

  const shiftCenters = _.sortBy(Object.values(orgConfig.shiftCenters).filter(sc => sc !== undefined), "name");

  return (
    <Card elevation={3} sx={{ width: 600 }}>
      <Header onClose={onClose} />
      <CardContent>
        <Grid container direction="row" spacing={2} sx={{ mt: 3 }}>
          <Grid size={5}>
            <Select
              sx={{ width: "100%" }}
              size="small"
              value={providerId?.toString()}
              onChange={e => setProviderId(parseInt(e.target.value, 10))}
            >
              {_.sortBy(providers, "firstName").map(p => <MenuItem key={p.id} value={p.id.toString()}>{p.firstName} {p.lastName}</MenuItem>)}
            </Select>
          </Grid>

          <Grid size={7}>
            <StartEndTime
              mode="datetime"
              startTime={startTime}
              endTime={endTime}
              onChange={(start, end) => { setStartTime(start); setEndTime(end); }}
              timezone={provider.addressTimezone ?? undefined}
            />
          </Grid>

          <Grid size={5}>
            <LabeledSelect
              size="small"
              label="Visits"
              fullWidth
              required
              value={providerTravel}
              onChange={(e) => setProviderTravel(e.target.value as ShiftProviderTravelEnum)}
            >
              <MenuItem value="inHomeOnly">In-Home</MenuItem>
              <MenuItem value="virtualOnly">Virtual</MenuItem>
              <MenuItem value="either">In-Home or Virtual</MenuItem>
            </LabeledSelect>
          </Grid>

          <Grid size={7}>
            <FormControlLabel
              label={<>Overflow Shift <Help text="The scheduler screen will only suggest using overflow shifts for prioritized visit types where the scheduler requests it"/></>}
              control={<Switch
                checked={overflowShift}
                onChange={(e) => setOverflowShift(e.target.checked)}
              />}
            />
          </Grid>

          <Grid size={5}>
            <LabeledSelect
              label="Shift Center"
              size="small"
              fullWidth
              value={shiftCenterId?.toString() || "none"}
              onChange={(e) => setShiftCenterId(e.target.value === "none" ? null : parseInt(e.target.value, 10))}
            >
              <MenuItem value="none">
                <em>Provider home</em>
              </MenuItem>
              {shiftCenters.map((center) => (
                <MenuItem key={center.id} value={center.id.toString()}>
                  {center.name}
                </MenuItem>
              ))}
            </LabeledSelect>
          </Grid>

          <Grid size={7}>
            {shiftCenterId && <FormControlLabel
              label={<>Use for drive times <Help text="The shift center will be used to calculate star/end or shift drive times, rather than the provider's home address"/></>}
              control={<Switch
                checked={useShiftCenterDriveTimes}
                onChange={(e) => setUseShiftCenterDriveTimes(e.target.checked)}
              />}
            />}
          </Grid>
        </Grid>

        {shiftTooShort && <>
          <WarningAmberRoundedIcon color="error" sx={{ verticalAlign: "bottom", mr: 0.25 }} /> Shifts must be at least one hour long
        </>}
      </CardContent>

      <CardActions sx={{ justifyContent: "end", borderTop: 1, borderColor: "divider" }}>
        <Button color="primary" onClick={onSave} loading={loading} disabled={shiftTooShort || !providerTravel}>Add Shift</Button>
      </CardActions>
    </Card>
  );
};

const Header = ({ onClose }: { onClose: () => void }) => (<CardHeader
  sx={{
    borderBottom: 1,
    borderColor: "divider",
    flex: 1,
    flexDirection: "row",
  }}
  action={<IconButton aria-label="close" onClick={onClose}>
    <Close />
  </IconButton>}
  title={<Typography variant="h6">Add shift</Typography>}
/>);

export default AddShift;

gql(`
  mutation CreateShift(
    $providerId: Int!
    $startTime: String!
    $endTime: String!
    $addressStreet: String
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $addressCoordinates: String!
    $addressTimezone: String
    $providerTravel: ShiftProviderTravelEnum!
    $shiftCenterId: Int
    $overflowShift: Boolean!
    $useShiftCenterDriveTimes: Boolean!
  )
  @permission(requires: manageProviderShifts)
  {
    createShift(
      status: confirmed
      providerId: $providerId
      startTime: $startTime
      endTime: $endTime
      addressStreet: $addressStreet
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressCoordinates: $addressCoordinates
      addressTimezone: $addressTimezone
      providerTravel: $providerTravel
      shiftCenterId: $shiftCenterId
      overflowShift: $overflowShift
      useShiftCenterDriveTimes: $useShiftCenterDriveTimes
    ) { id }
  }
`);
